import { inject, Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { ValidateCrmObjectResponseValidationErrorInterface } from '@vendasta/crm';
import { BehaviorSubject, Observable, of, switchMap, take } from 'rxjs';
import { debounceTime, map, shareReplay, tap } from 'rxjs/operators';
import { ValidationError } from './field-validation';
import { ObjectType, RenderableFieldInterface } from '../../tokens-and-interfaces';
import { ValidateErrorsWithFieldSchemas, CrmFormService } from '../../shared-services';

type ServerValidationError = ValidateCrmObjectResponseValidationErrorInterface;

function uniquenessInvalidErrors(errors: ServerValidationError[], fieldId: string): ValidationErrors | null {
  const uniquenessErrorForField = errors.find(
    (e) => e.ruleId === 'uniqueness_enforced' && e.details?.uniquenessCheck?.fieldId === fieldId,
  );
  return uniquenessErrorForField ? { uniqueness_enforced: true } : null;
}

function fieldSchemaErrors(errors: ServerValidationError[], fieldId: string): ValidationErrors | null {
  const fieldSchemaErrorForField = errors.find(
    (e) => e.ruleId === 'field_schema_validation' && e.details?.fieldIds?.includes(fieldId),
  );
  if (!fieldSchemaErrorForField) {
    return null;
  }

  // Expected format: "VALIDATION_ERROR.RESTRICTED_VALUE", "VALIDATION_ERROR.EMPTY_FIELD", etc.
  const message = fieldSchemaErrorForField.message;
  if (message && message.startsWith('VALIDATION_ERROR.')) {
    const errorType = message.replace('VALIDATION_ERROR.', '').toLowerCase();
    return { [`field_schema_validation_${errorType}`]: true };
  }
  return { field_schema_validation: true };
}

/** Maps server-side validation errors to the control error keys the field renderers display. */
export function controlErrorsForField(errors: ServerValidationError[], fieldId: string): ValidationErrors | null {
  const controlErrors = { ...fieldSchemaErrors(errors, fieldId), ...uniquenessInvalidErrors(errors, fieldId) };
  return Object.keys(controlErrors).length > 0 ? controlErrors : null;
}

const uniquenessInvalidValidatorFn =
  (validationChanges$: Observable<ValidateErrorsWithFieldSchemas>, fieldId: string) => () => {
    return validationChanges$.pipe(
      take(1),
      map((validationErrors) => uniquenessInvalidErrors(validationErrors.errors, fieldId)),
    );
  };

const uniquenessWarningValidatorFn =
  (validationChanges$: Observable<ValidateErrorsWithFieldSchemas>, fieldId: string) => () => {
    return validationChanges$.pipe(
      take(1),
      map((validationErrors) => {
        const uniquenessErrorForField = validationErrors.errors.find(
          (e) => e.ruleId === 'uniqueness_warning' && e.details?.uniquenessCheck?.fieldId === fieldId,
        );
        if (!uniquenessErrorForField) {
          return null;
        }
        const existingObjectIds = uniquenessErrorForField.details?.uniquenessCheck?.existingCrmObjectIds;
        const hasErrors = existingObjectIds && existingObjectIds.length > 0;
        if (hasErrors) {
          return {
            uniqueness_warning: true,
          };
        }
        return null;
      }),
    );
  };

const fieldSchemaValidatorFn =
  (validationChanges$: Observable<ValidateErrorsWithFieldSchemas>, fieldId: string) => () => {
    return validationChanges$.pipe(
      take(1),
      map((validationErrors) => fieldSchemaErrors(validationErrors.errors, fieldId)),
    );
  };

@Injectable()
export class ValidatorFactoryService {
  private readonly formService = inject(CrmFormService);

  private objectType!: ObjectType;
  private valueChanges$: Observable<boolean> = of(false);
  private renderableFields: RenderableFieldInterface[] = [];
  private validationChanges$!: Observable<ValidateErrorsWithFieldSchemas>;

  latestValidationErrors$$: BehaviorSubject<ValidateErrorsWithFieldSchemas | null> =
    new BehaviorSubject<ValidateErrorsWithFieldSchemas | null>(null);

  initialize(
    objectType: ObjectType,
    existingObjectId: string,
    renderableFields: RenderableFieldInterface[],
    valueChanges$: Observable<any>,
  ): void {
    this.objectType = objectType;
    this.valueChanges$ = valueChanges$.pipe(map((v) => !!v));
    this.renderableFields = renderableFields;
    this.validationChanges$ = this.valueChanges$.pipe(
      debounceTime(300),
      switchMap(() => {
        const fields = this.renderableFields.map((r) => r.fieldValue());
        return this.formService.validateCrmObject$(this.objectType, {
          crmObjectId: existingObjectId,
          fields,
        });
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      // request for validation happens in validator before loading
      // shared-components dependent upon validation data, cache manually to handle scenario
      // for uniqueness suggestions
      tap((v) => this.latestValidationErrors$$.next(v)),
    );
  }

  buildUniquenessValidatorCodeMappings(fieldId: string): { [key: string]: ValidationError } {
    return {
      uniqueness_enforced: {
        severity: 'INVALID',
        message: 'STANDARD_FIELD_RULES.UNIQUE_FIELD_VALUE.ENFORCED',
        validationFn: null,
        asyncValidationFn: uniquenessInvalidValidatorFn(this.validationChanges$, fieldId),
      },
      uniqueness_warning: {
        severity: 'WARNING',
        message: 'STANDARD_FIELD_RULES.UNIQUE_FIELD_VALUE.WARNING',
        validationFn: null,
        asyncValidationFn: uniquenessWarningValidatorFn(this.validationChanges$, fieldId),
      },
    };
  }

  buildFieldSchemaValidatorCodeMappings(fieldId: string): { [key: string]: ValidationError } {
    const validationErrorTypes = [
      'RESTRICTED_VALUE',
      'EMPTY_FIELD',
      'FIELD_WITHOUT_IDENTIFICATION',
      'DUPLICATE_FIELD_ID',
      'FIELD_DEFINITION_NOT_FOUND',
      'UNSUPPORTED_FIELD_TYPE',
      'INVALID_VALUE_TYPE',
      'TAG_EXCEEDS_LIMIT',
      'TAG_CANNOT_BE_EMPTY',
      'INVALID_EMAIL',
      'INVALID_CURRENCY_CODE',
      'PHONE_NATIONAL_NUMBER_EXCEEDS_LIMIT',
      'PHONE_COUNTRY_CODE_EXCEEDS_LIMIT',
      'PHONE_EXTENSION_EXCEEDS_LIMIT',
      'STRING_EXCEEDS_LIMIT',
      'STRING_LIST_ITEM_EXCEEDS_LIMIT',
      'INVALID_URL',
      'GEOPOINT_LATITUDE_OUT_OF_RANGE',
      'GEOPOINT_LONGITUDE_OUT_OF_RANGE',
    ];

    const mappings: { [key: string]: ValidationError } = {};

    // Build mappings for each validation error type
    for (const errorType of validationErrorTypes) {
      const key = `field_schema_validation_${errorType.toLowerCase()}`;
      mappings[key] = {
        severity: 'INVALID',
        message: `VALIDATION_ERROR.${errorType}`,
        validationFn: null,
        asyncValidationFn: fieldSchemaValidatorFn(this.validationChanges$, fieldId),
      };
    }

    // Add fallback mapping for unexpected error types
    mappings['field_schema_validation'] = {
      severity: 'INVALID',
      message: 'VALIDATION_ERROR.RESTRICTED_VALUE',
      validationFn: null,
      asyncValidationFn: fieldSchemaValidatorFn(this.validationChanges$, fieldId),
    };

    return mappings;
  }
}
